import React from 'react'
import { QueryKey, useMutation, useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { useClient } from '@hooks/useClient'
import { useAlert } from '@hooks/useAlert'
import { getErrorMessage } from '@libs/utils'
import { queryClient } from '@client/init'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { PromotionChallengeDetailPageProps } from './interface'

export function withPromotionChallengeDetailPage(
  Component: React.FC<PromotionChallengeDetailPageProps>
) {
  function Hoc() {
    const navigate = useNavigate()
    const { id } = useParams()
    const client = useClient()
    const alert = useAlert()
    const queryKey: QueryKey = ['promotion', 'challenge', Number(id)]
    const confirmModal = useModal({ modal: 'confirm' })

    function handleGoToHistoryPage() {
      navigate(`/promotion-setting/promotion-challenge/${id}/history`)
    }

    const { data, isLoading } = useQuery(
      [...queryKey, 'detail'],
      () =>
        client?.promotionChallengeClient.getPromotionChallengeDetail(
          Number(id)
        ),
      {
        onError: () => navigate('/promotion-setting'),
      }
    )

    const { mutateAsync: togglePromotionIsActive } = useMutation(
      ({ isActive, promotionId }: { isActive: boolean; promotionId: number }) =>
        client!.promotionClient.togglePromotionIsActive(isActive, promotionId),
      {
        onSuccess: () => {
          alert.success('ดำเนินการสำเร็จ')
          queryClient.invalidateQueries(queryKey)
        },
        onError: error => {
          alert.error(getErrorMessage(error))
        },
      }
    )

    function handleActivePromotion(isActive: boolean) {
      if (id && Number(id)) {
        togglePromotionIsActive({ isActive, promotionId: Number(id) })
      }
    }

    const { mutateAsync: deletePromotion } = useMutation(
      (promotionId: number) =>
        client!.promotionClient.deletePromotion(promotionId),
      {
        onSuccess: () => {
          alert.success('ลบโปรโมชั่นสำเร็จ')
          queryClient.invalidateQueries(['get-promotions'])
          navigate(`/promotion-setting`)
        },
        onError: error => {
          alert.error(getErrorMessage(error))
        },
      }
    )

    function handleDeletePromotion() {
      if (id && Number(id)) {
        confirmModal.show({
          content: (
            <div className='w-[280px]'>
              <p className='font-mitr text-center text-[24px] text-secondary font-medium '>
                ลบโปรโมชั่น
              </p>
              <div className='flex flex-col text-center  mt-[20px] mb-[30px] text-[14px] font-light text-secondary'>
                คุณกำลังจะลบโปรโมชั่น
                <span className='font-bold'>&ldquo;{data?.name} &rdquo;</span>
                ต้องการที่จะดำเนินการต่อหรือไม่
              </div>
            </div>
          ),
          onConfirm: async () => {
            await deletePromotion(Number(id))
            confirmModal.hide()
          },
          onClose: () => {
            confirmModal.hide()
          },
          confirmButtonText: 'ยืนยัน',
        })
      }
    }

    function handleEditPromotion() {
      navigate(`/promotion-setting/promotion-challenge/${id}/edit`)
    }

    const newProps: PromotionChallengeDetailPageProps = {
      detail: data,
      isLoading,
      handleActivePromotion,
      handleGoToHistoryPage,
      handleDeletePromotion,
      handleEditPromotion,
    }
    return <Component {...newProps} />
  }
  return Hoc
}
